import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer } from '@sm/ide-components';
import { useChatStore } from '@/store';
import { useIsMobileViewport } from '@/hooks';
import ChatPanel from '../ChatPanel';
import { InboundInit } from '@/types';
import { applyInitPayload } from '@/utils/applyInitPayload';
import { MACAW_ROOT_ID } from '@/utils/macawRoot';
import './index.less';

const IGNORE_MASK_CLOSE_MS = 400;

interface ChatDrawerProps {
  container?: HTMLElement | null;
  options: InboundInit['payload'];
  /** 由外部完成 init 时传入，跳过内部 initRoles */
  isReady?: boolean;
}

function resolveDrawerConfig(
  config: InboundInit['payload']['config'] | undefined,
  isMobile: boolean,
  isSdk: boolean,
): InboundInit['payload']['config'] {
  return {
    history: true,
    ...config,
    close: true,
    /** SDK H5 抽屉已是满宽，不再显示全屏 */
    fullScreen: isSdk && isMobile ? false : true,
  };
}

/**
 * ChatDrawer — 悬浮按钮 / widget 模式
 *
 * 内置 Drawer，宽度固定 600px（移动端自动 100vw）。
 * 配合 AiFloatButton 使用。
 */
const ChatDrawer: React.FC<ChatDrawerProps> = (props) => {
  const { container, options, isReady: isReadyProp } = props;
  const isOpen = useChatStore((s) => s.isOpen);
  const closeDrawer = useChatStore((s) => s.closeDrawer);
  const [internalReady, setInternalReady] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const initRoles = useChatStore((s) => s.initRoles);
  const controlledReady = isReadyProp !== undefined;
  const isReady = controlledReady ? isReadyProp : internalReady;
  const isMobile = useIsMobileViewport();
  const isSdk = container?.id === MACAW_ROOT_ID;
  const config = resolveDrawerConfig(options.config, isMobile, isSdk);
  const drawerWidth = isMobile || isFullScreen ? '100%' : 600;
  const ignoreCloseUntilRef = useRef(0);

  useEffect(() => {
    if (!isOpen || isMobile) setIsFullScreen(false);
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen) {
      ignoreCloseUntilRef.current = Date.now() + IGNORE_MASK_CLOSE_MS;
    }
  }, [isOpen]);

  useEffect(() => {
    if (controlledReady) return;

    let cancelled = false;
    applyInitPayload(options);
    void initRoles(options?.solution_code as string)
      .catch((err) => {
        console.error('[initRoles] failed:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setInternalReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [options, controlledReady, initRoles]);

  const handleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  const handleDrawerClose = useCallback(() => {
    if (Date.now() < ignoreCloseUntilRef.current) return;
    closeDrawer();
  }, [closeDrawer]);

  return (
    <Drawer
      rootClassName={`chat-bi-drawer${isFullScreen ? ' chat-bi-drawer--fullscreen' : ''}`}
      open={isOpen}
      onClose={handleDrawerClose}
      placement="right"
      width={drawerWidth}
      closable={false}
      zIndex={1000}
      forceRender
      autoFocus={false}
      push={false}
      getContainer={false}
      drawerStyle={{ background: '#fff' }}
    >
      <ChatPanel
        config={config}
        hostSource={options.source ?? ''}
        onClose={handleDrawerClose}
        isReady={isReady}
        onFullScreen={handleFullScreen}
        impressionOnOpen
      />
    </Drawer>
  );
};

export default ChatDrawer;
